import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Trash2, Loader2, CalendarDays, Clock, Star, Lock, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/app/StatusBadge";
import { AudioPlayer } from "@/components/meeting/AudioPlayer";
import { SummaryTab } from "@/components/meeting/SummaryTab";
import { DocumentsTab } from "@/components/meeting/documents/DocumentsTab";
import { MeetingTabsNav, NextStepBanner } from "@/components/meeting/MeetingTabsNav";
import { TranscriptTab } from "@/components/meeting/TranscriptTab";
import { MetaBar } from "@/components/meeting/MetaBar";
import { BotCard, BOT_ACTIVE } from "@/components/bot/BotCard";
import { api, fmtTime, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { UpgradeLock } from "@/components/billing/UpgradeLock";
import { ProcessingProgress } from "@/components/app/ProcessingProgress";
import { alertDone } from "@/components/app/recordingAlert";
import { isPaid } from "@/config/plans";
import { useAuth } from "@/context/AuthContext";

const Header = ({ data, onDelete, onFav }) => {
  const { L } = useApp();
  const m = data.meeting;
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={m.status} />
          {data.notes && <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800" data-testid="draft-badge">{L("DRAF — untuk semakan", "DRAFT — for review")}</span>}
          <span className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">{m.visibility === "private" ? <Lock size={11} /> : <Users size={11} />} {m.visibility === "private" ? L("Peribadi", "Private") : "Workspace"}</span>
        </div>
        <h1 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="meeting-title">{m.title}</h1>
        <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
          <span className="flex items-center gap-1"><CalendarDays size={14} /> {m.meeting_date}</span>
          <span className="flex items-center gap-1"><Clock size={14} /> {fmtTime(m.duration)}</span>
          {m.participants?.length > 0 && <span>{m.participants.join(", ")}</span>}
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="icon" className="rounded-xl" onClick={onFav} aria-label="Kegemaran" data-testid="favourite-btn"><Star size={16} className={m.favourite ? "fill-amber-400 text-amber-400" : ""} /></Button>
        {data.role !== "viewer" && (
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" size="icon" className="rounded-xl text-red-600" aria-label="Padam" data-testid="delete-meeting-btn"><Trash2 size={16} /></Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{L("Padam mesyuarat ini?", "Delete this meeting?")}</AlertDialogTitle>
                <AlertDialogDescription>{L("Transkrip, minit dan pautan kongsi akan dibatalkan.", "Transcript, minutes and share links will be removed.")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel-btn">{L("Batal", "Cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">{L("Padam", "Delete")}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
};

export default function MeetingDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { L } = useApp();
  const { user, workspace } = useAuth();
  const paid = isPaid(workspace);
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState(params.get("t") ? "transcript" : "summary");
  const [current, setCurrent] = useState(-1);
  const [docKind, setDocKind] = useState(null);
  const player = useRef(null);

  const load = useCallback(() => api.get(`/meetings/${id}`).then((r) => { setData(r.data); }).catch(() => navigate("/app/meetings")), [id, navigate]);
  useEffect(() => { load(); api.get("/templates").then((r) => setTemplates(r.data)); }, [load]);
  useEffect(() => {
    if (!data || ["completed", "failed"].includes(data.meeting.status)) return;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [data, load]);
  const status = data?.meeting.status;
  const prevStatus = useRef(null);
  useEffect(() => {
    const was = prevStatus.current;
    prevStatus.current = status;
    if (!was || was === status || ["completed", "failed", "bot_scheduled"].includes(was)) return;
    if (status === "completed") alertDone(L("Transkripsi siap!", "Transcription ready!"), data.meeting.title, window.location.pathname);
    if (status === "failed") alertDone(L("Pemprosesan gagal", "Processing failed"), data.meeting.title, window.location.pathname);
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const docType = data?.meeting.doc_type;
  const hasNotes = !!data?.notes;
  useEffect(() => {
    if (status === "completed" && docType && !params.get("t") && paid) setTab("documents");
    else if (status === "completed" && !hasNotes) setTab((t) => (t === "summary" ? "transcript" : t));
  }, [status, docType, params, paid, hasNotes]);
  useEffect(() => {
    const t = params.get("t");
    if (t && data?.files?.some((f) => f.kind === "processed")) setTimeout(() => player.current?.seek(Number(t)), 800);
  }, [params, data?.files]);

  if (!data) return <div className="grid h-96 place-items-center"><Loader2 className="animate-spin text-violet-600" /></div>;
  const m = data.meeting;
  const canWrite = data.role !== "viewer" && (data.role !== "member" || m.created_by === user.id);
  const canShare = canWrite && (m.created_by === user.id || ["owner", "admin"].includes(data.role));
  const seek = (t) => player.current?.seek(t);
  const hasAudio = data.files.some((f) => f.kind === "processed");

  const retry = async () => { try { await api.post(`/meetings/${id}/retry`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const remove = async () => { await api.delete(`/meetings/${id}`); toast.success(L("Mesyuarat dipadam", "Meeting deleted")); navigate("/app/meetings"); };
  const fav = async () => { await api.patch(`/meetings/${id}`, { favourite: !m.favourite }); load(); };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8" data-testid="meeting-detail-page">
      <Header data={data} onDelete={remove} onFav={fav} />
      <MetaBar meeting={m} canWrite={canWrite} onChange={load} />
      {hasAudio && <div className="sticky top-14 z-20 mt-6 rounded-2xl border border-slate-200 bg-white/95 p-3 backdrop-blur lg:top-2"><AudioPlayer ref={player} src={`/meetings/${id}/audio`} onTime={setCurrent} /></div>}
      <div className="mt-6">
        {m.status !== "completed" && <ProcessingProgress status={m.status} error={m.error} onRetry={retry} canWrite={canWrite && !(m.bot && !data.job)} progress={data.job?.progress} bot={!!m.bot} since={data.job?.created_at || m.created_at} duration={m.duration} />}
        {m.bot && BOT_ACTIVE.includes(m.status) && <BotCard meeting={m} canWrite={canWrite} onChange={load} className="mt-4" />}
        {data.segments.length > 0 && m.status === "completed" && tab !== "documents" && <NextStepBanner meeting={m} canGenerate={paid && canWrite} onDone={(k) => { setDocKind(k); setTab("documents"); }} />}
        {data.segments.length > 0 && (
          <Tabs value={tab} onValueChange={setTab} className="mt-6">
            <MeetingTabsNav tab={tab} hasNotes={!!data.notes} highlightDocs={m.status === "completed"} />
            <TabsContent value="summary" className="mt-5"><SummaryTab notes={data.notes} onSeek={seek} /></TabsContent>
            <TabsContent value="transcript" className="mt-5"><TranscriptTab meeting={m} segments={data.segments} onSeek={seek} current={current} canWrite={canWrite} onNames={(n) => setData({ ...data, meeting: { ...m, speaker_names: n } })} onSegments={(segs) => setData({ ...data, segments: segs })} onReload={load} onMeeting={(p) => setData({ ...data, meeting: { ...m, ...p } })} /></TabsContent>
            <TabsContent value="documents" className="mt-5">{paid ? <DocumentsTab key={`${m.status}-${docKind}`} meeting={m} canWrite={canWrite} initialKind={docKind} /> : <UpgradeLock testId="documents-lock" title={L("Jana Minit Rasmi, Nota Kuliah, Laporan & Temubual", "Generate Official Minutes, Lecture Notes, Reports & Interviews")} desc={L("Pelan Percuma hanya merangkumi rakaman, muat naik dan transkrip. Naik taraf (mulai Lite RM19.90/bulan) untuk menjana dokumen, mengeksport ke PDF/DOCX/PPTX dan berkongsi melalui WhatsApp atau Emel.", "The Free plan covers recording, upload and transcript only. Upgrade (from Lite RM19.90/month) to generate documents, export to PDF/DOCX/PPTX and share via WhatsApp or Email.")} />}</TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
